/**
 * Ordering Content Type Schema
 * A content type with title/description display and ordering questions
 */

import { generateId } from '@ai-content-hub/content-core';
import type {
    CompositeQuestionsLayoutFields,
    ContentConfig,
    LayoutConfig,
    ScoringConfig,
} from '@ai-content-hub/content-core';
import type { OrderingElementData, PageElementData } from '@ai-content-hub/content-elements';
import { defaultPageData, normalizeOrderingData } from '@ai-content-hub/content-elements';

/**
 * Stable id for the exercise synthesized when `exercises` is empty — see
 * fill-in-the-blanks schema (same generateId-on-normalize pitfall).
 */
const ORDERING_CONTENT_DEFAULT_EXERCISE_ID = 'a0000000-0000-4000-8000-000000000002';

/**
 * Player view layout
 * - stacked: Title/description on top, ordering below (default)
 * - compact: Title inline, ordering below
 */
export type OrderingPlayerView = 'stacked' | 'compact';

/**
 * Layout configuration for Ordering content type
 */
export interface OrderingLayoutConfig extends LayoutConfig {
    /** Player view layout */
    playerView?: OrderingPlayerView;
}

/**
 * Scoring configuration for Ordering content type
 */
export interface OrderingScoringConfig extends ScoringConfig {
    /** Points per correct position (default: 1) */
    pointsPerPosition?: number;
    /** Passing threshold (0-1) */
    passingThreshold?: number;
}

/**
 * Ordering content type config
 */
export interface OrderingContentConfig extends Omit<ContentConfig, 'layout' | 'scoring'> {
    layout?: OrderingLayoutConfig;
    scoring?: OrderingScoringConfig;
}

/**
 * One ordering task in the Questions tab
 */
export interface OrderingContentExercise {
    id: string;
    data: OrderingElementData;
}

/**
 * Ordering content type data — wraps ordering element(s) with title/description
 */
export interface OrderingContentData extends CompositeQuestionsLayoutFields {
    /** Title displayed above the ordering exercise */
    title?: string;
    /** Description / instructions */
    description?: string;
    /** Intro / lesson blocks before the ordering task (editor Content tab) */
    lessonContent: PageElementData;
    /** One or more ordering exercises */
    exercises: OrderingContentExercise[];
    /**
     * Legacy single exercise — normalized into `exercises` on read.
     * Do not set when saving; use `exercises` only.
     */
    orderingData?: OrderingElementData;
}

/**
 * Complete ordering content item
 */
export interface OrderingContent {
    id: string;
    type: 'ordering';
    version: string;
    data: OrderingContentData;
    config?: OrderingContentConfig;
}

/**
 * Default i18n translations
 */
export const defaultOrderingI18n = {
    en: {
        title: 'Ordering',
        instructions: 'Drag and drop to arrange items in the correct order',
        submit: 'Submit',
        retry: 'Try Again',
        score: 'Score',
        passed: 'Passed!',
        failed: 'Keep practicing',
        contentTab: 'Content',
        questionsTab: 'Questions',
        configTab: 'Configuration',
        titleLabel: 'Title',
        titlePlaceholder: 'Enter title',
        descLabel: 'Instructions (Optional)',
        descPlaceholder: 'Enter instructions...',
        questionsListTitle: 'Questions',
        emptyQuestionsMsg: 'No questions yet. Add an ordering exercise.',
        addSection: 'Add section',
        removeSection: 'Remove section',
        sectionDetailTitle: 'Question section',
        addExerciseLabel: 'Add exercise',
        addExerciseOption: 'Ordering',
        detailEmptyMessage: 'Select a question, a section, or add one to start.',
        exercisePreview: 'Question {{index}} — {{n}} items',
        questionDetailTitle: 'Question',
    },
    vi: {
        title: 'Sắp xếp',
        instructions: 'Kéo thả để sắp xếp các mục theo đúng thứ tự',
        submit: 'Gửi câu trả lời',
        retry: 'Thử lại',
        score: 'Điểm',
        passed: 'Đã vượt qua!',
        failed: 'Tiếp tục luyện tập',
        contentTab: 'Nội dung',
        questionsTab: 'Câu hỏi',
        configTab: 'Cấu hình',
        titleLabel: 'Tiêu đề',
        titlePlaceholder: 'Nhập tiêu đề bài tập sắp xếp',
        descLabel: 'Hướng dẫn bài tập (Tuỳ chọn)',
        descPlaceholder: 'Nhập hướng dẫn bài tập...',
        questionsListTitle: 'Câu hỏi',
        emptyQuestionsMsg: 'Chưa có câu. Thêm câu sắp xếp.',
        addSection: 'Thêm nhóm',
        removeSection: 'Xóa nhóm',
        sectionDetailTitle: 'Nhóm câu hỏi',
        addExerciseLabel: 'Thêm câu sắp xếp',
        addExerciseOption: 'Sắp xếp thứ tự',
        detailEmptyMessage: 'Chọn một câu, một nhóm, hoặc thêm mới.',
        exercisePreview: 'Câu {{index}} — {{n}} mục',
        questionDetailTitle: 'Câu',
    },
};

/**
 * Default config
 */
export const defaultOrderingContentConfig: OrderingContentConfig = {
    layout: {
        type: 'vertical',
        playerView: 'stacked',
    },
    behavior: {
        allowRetry: true,
        showCorrectAnswer: true,
        showExplanation: true,
    },
    scoring: {
        pointsPerPosition: 1,
        partialCreditEnabled: true,
        passingThreshold: 0.7,
    },
    i18n: {
        defaultLanguage: 'en',
        languages: defaultOrderingI18n,
    },
};

/**
 * Default data
 */
export const defaultOrderingContentData: OrderingContentData = {
    title: '',
    description: '',
    lessonContent: { ...defaultPageData },
    exercises: [],
};

/**
 * Merge legacy `orderingData` and ensure at least one exercise.
 */
export function normalizeOrderingContentData(
    data: Partial<OrderingContentData> | null | undefined,
): OrderingContentData {
    const lessonContent = data?.lessonContent ?? { ...defaultPageData };
    let exercises = data?.exercises;
    if (!exercises?.length) {
        if (data?.orderingData) {
            exercises = [
                {
                    id: ORDERING_CONTENT_DEFAULT_EXERCISE_ID,
                    data: normalizeOrderingData(data.orderingData),
                },
            ];
        } else {
            exercises = [
                {
                    id: ORDERING_CONTENT_DEFAULT_EXERCISE_ID,
                    data: normalizeOrderingData({}),
                },
            ];
        }
    } else {
        exercises = exercises.map((e) => ({
            id: e.id?.trim() ? e.id : generateId(),
            data: normalizeOrderingData(e.data ?? {}),
        }));
    }
    const questionsSidebarRows = data?.questionsSidebarRows;
    return {
        title: data?.title ?? '',
        description: data?.description ?? '',
        lessonContent,
        exercises,
        ...(Array.isArray(questionsSidebarRows) && questionsSidebarRows.length > 0
            ? { questionsSidebarRows }
            : {}),
    };
}

export function serializableOrderingContentData(data: OrderingContentData): OrderingContentData {
    const n = normalizeOrderingContentData(data);
    return {
        title: n.title,
        description: n.description,
        lessonContent: n.lessonContent,
        exercises: n.exercises,
        ...(n.questionsSidebarRows?.length ? { questionsSidebarRows: n.questionsSidebarRows } : {}),
    };
}

/**
 * Schema definition
 */
export const orderingContentSchema = {
    type: 'ordering' as const,
    title: 'Ordering',
    description: 'Drag-and-drop ordering exercise with title and instructions',
    version: '1.0.0',
    category: 'assessment' as const,
    icon: 'ListOrdered',
    tags: ['ordering', 'sequence', 'drag-drop', 'assessment'],
    defaultData: defaultOrderingContentData,
    defaultConfig: defaultOrderingContentConfig,
};
